import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useHeightContext, useTranslation } from '@eti/providers';
import { css, cx, mediaQueries } from '@eti/styles';
import { Overlay } from '@eti/components';
import { useToggle } from '../../../../common/custom-hooks/toggle';
import FixedFooter from './FixedFooter';

const getOverlayContentStyles = (footerHeight) => css`
  border-top: 1px solid #e9e9e9;
  margin-bottom: ${footerHeight}px;
  padding: 0;

  @media ${mediaQueries.medium.up} {
    margin-bottom: 0;
    padding: 0;
  }
`;

const overlayBackdropStyles = css`
  &::after {
    opacity: 1;
  }
`;

const cartOverlayWrapperStyles = (isOpen, footerHeight) => css`
  background: #fff;
  bottom: 0;
  left: 0;
  position: fixed;
  right: 0;
  transform: translateY(${isOpen ? '0' : '100%'});
  transition-property: margin-bottom, transform;
  z-index: 12;

  @media ${mediaQueries.medium.up} {
    margin-bottom: ${isOpen ? footerHeight : 0}px;
    max-height: calc(100vh - ${footerHeight}px);
  }
`;

const MobileCart = ({ addedProductsNumber, children, totalPrice }) => {
  const { t } = useTranslation();
  const { handleMobileCartHeightContext } = useHeightContext();
  const fixedFooterRef = useRef();
  const cartRef = useRef();
  const { isOpen: cartIsOpen, handleToggle: toggleCartIsOpen } = useToggle(false);
  const [footerCurrentHeight, setFooterCurrentHeight] = useState(0);

  useEffect(() => {
    const { height: mobileCartHeight } = fixedFooterRef.current.getBoundingClientRect();
    setFooterCurrentHeight(mobileCartHeight);
    handleMobileCartHeightContext(mobileCartHeight);
    return () => {
      handleMobileCartHeightContext(0);
    };
  }, [cartIsOpen, cartRef, handleMobileCartHeightContext]);

  useEffect(() => {
    if (cartIsOpen) {
      document.body.style.overflow = 'hidden';
      cartRef.current.focus();
    } else {
      document.body.style.overflow = '';
    }
  }, [cartIsOpen]);

  return (
    <>
      {cartIsOpen && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
        <div
          className={cx(Overlay.BackdropStyles, overlayBackdropStyles)}
          onClick={toggleCartIsOpen}
        />
      )}
      <div
        className={cx(
          Overlay.ModalStyles(),
          cartOverlayWrapperStyles(cartIsOpen, footerCurrentHeight),
        )}
        data-testid="mobileCart-content"
        ref={cartRef}
        tabIndex="-1"
      >
        <Overlay.Header onClose={toggleCartIsOpen} title={t('Cart.Title')} />
        <Overlay.Content className={getOverlayContentStyles(footerCurrentHeight)}>
          {children}
        </Overlay.Content>
      </div>
      <FixedFooter
        addedProductsNumber={addedProductsNumber}
        footerHeight={footerCurrentHeight}
        footerRef={fixedFooterRef}
        isCartOpen={cartIsOpen}
        onClick={toggleCartIsOpen}
        totalPrice={totalPrice}
      />
    </>
  );
};

MobileCart.propTypes = {
  addedProductsNumber: PropTypes.number,
  children: PropTypes.node,
  totalPrice: PropTypes.number,
};

export default MobileCart;
